import { AsyncPipe, DatePipe } from '@angular/common'
import { ChangeDetectionStrategy, Component, inject, OnDestroy, OnInit } from '@angular/core'
import { MatDialog } from '@angular/material/dialog'
import { MatIcon } from '@angular/material/icon'
import { MatProgressSpinner } from '@angular/material/progress-spinner'
import {
  DataContextBuilder,
  ElderGridComponent,
  ElderGridTileDirective,
  ElderToastService,
  ElderToolbarColumnDirective,
  IDataContext,
  Interval,
  LocalListDataSource,
} from '@elderbyte/ngx-starter'
import { LoggerFactory } from '@elderbyte/ts-logger'
import { Instant } from '@js-joda/core'
import { ElderTranslatePipe } from '@elderbyte/ngx-starter'
import { BehaviorSubject, combineLatest, Observable, of, Subject, Subscription } from 'rxjs'
import { catchError, filter, map, mergeMap, takeUntil, tap } from 'rxjs/operators'
import { IntervalContextMenuComponent } from '../../../../context/interval-context/interval-context-menu/interval-context-menu.component'
import { MexusContext, MexusContextService } from '../../../../context/mexus-context.service'
import { MexusViewMode } from '../../../../context/view-mode-context/mexus-view-mode'
import { ViewModeContextMenuComponent } from '../../../../context/view-mode-context/view-mode-context-menu/view-mode-context-menu.component'
import { Facility } from '../../../../facilities/facility'
import { FacilityService } from '../../../../facilities/facility.service'
import { UtilisationCapacityBarComponent } from '../../utilisation-bar/utilisation-capacity-bar/utilisation-capacity-bar.component'
import { UtilisationProgressBarComponent } from '../../utilisation-bar/utilisation-progress-bar/utilisation-progress-bar.component'
import { UtilisationFilterBuilder } from '../../utilisation-filter-builder'
import { FacilityUtilisation } from '../facility-utilisation'
import {
  FacilityUtilisationDetailDialogComponent,
  IFacilityUtilisationDialogOptions,
} from '../facility-utilisation-detail-dialog/facility-utilisation-detail-dialog.component'
import { FacilityUtilisationService } from '../facility-utilisation.service'
import {
  AsyncFacilityUtilisation,
  AsyncFacilityUtilisationState,
} from './async-facility-utilisation'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'

@Component({
  selector: 'app-facility-utilisation-viewer',
  templateUrl: './facility-utilisation-viewer.component.html',
  styleUrls: ['./facility-utilisation-viewer.component.scss'],
  imports: [
    ElderToolbarColumnDirective,
    ViewModeContextMenuComponent,
    IntervalContextMenuComponent,
    ElderGridComponent,
    ElderGridTileDirective,
    MatIcon,
    MatProgressSpinner,
    UtilisationProgressBarComponent,
    UtilisationCapacityBarComponent,
    AsyncPipe,
    DatePipe,
    ElderTranslatePipe,
  ],
})
export class FacilityUtilisationViewerComponent implements OnInit, OnDestroy {
  /***************************************************************************
   *                                                                         *
   * Fields                                                                  *
   *                                                                         *
   **************************************************************************/

  private readonly log = LoggerFactory.getLogger(this.constructor.name)
  private readonly _activeContext$ = new BehaviorSubject<MexusContext>(null)
  private readonly _facilities$ = new BehaviorSubject<Facility[]>([])
  private readonly _utilisationsDataSrc: LocalListDataSource<AsyncFacilityUtilisation>
  private readonly _utilisationReloadQueue = new Subject<AsyncFacilityUtilisation>()
  private readonly _utilisationRequest: Subscription
  private facilityService = inject(FacilityService)
  private facilityUtilisationService = inject(FacilityUtilisationService)
  private appContextService = inject(MexusContextService)
  private matDialog = inject(MatDialog)
  private toastService = inject(ElderToastService)
  public readonly dataContext: IDataContext<AsyncFacilityUtilisation>
  public readonly ViewMode = MexusViewMode
  public readonly AsyncState = AsyncFacilityUtilisationState

  /***************************************************************************
   *                                                                         *
   * Constructor                                                             *
   *                                                                         *
   **************************************************************************/

  constructor() {
    this.loadFacilities()
    this.loadMexusContext()

    // Data Source
    this._utilisationsDataSrc = LocalListDataSource.empty('id')

    // Data Context
    this.dataContext = DataContextBuilder.start<AsyncFacilityUtilisation>()
      .reloadOnLocalChanges()
      .build(this._utilisationsDataSrc)

    this.setUpRequestBuildingPipeline()
    this.setUpRequestProcessingPipeline()
  }

  /***************************************************************************
   *                                                                         *
   * Life Cycle                                                              *
   *                                                                         *
   **************************************************************************/

  public ngOnInit(): void {
    this.dataContext.start()
  }

  public ngOnDestroy(): void {
    this.cancelUtilisationRequest()
  }

  /***************************************************************************
   *                                                                         *
   * Properties                                                              *
   *                                                                         *
   **************************************************************************/

  public get ctx$(): Observable<MexusContext> {
    return this._activeContext$.asObservable()
  }

  public get ctx(): MexusContext {
    return this._activeContext$.getValue()
  }

  /***************************************************************************
   *                                                                         *
   * Public API                                                              *
   *                                                                         *
   **************************************************************************/

  public openShopfloorUtilisation($event: AsyncFacilityUtilisation) {
    if ($event) {
      this.openDetail(
        $event.facility,
        this.ctx?.interval,
        this.ctx?.liveTimestamp,
        this.ctx?.viewMode
      )
    }
  }

  public percent(value: number): string {
    return Math.round(value * 100) + '%'
  }

  coerceUtilization(utilization: any): AsyncFacilityUtilisation {
    return <AsyncFacilityUtilisation>utilization
  }

  /***************************************************************************
   *                                                                         *
   * Private Methods                                                         *
   *                                                                         *
   **************************************************************************/

  private loadFacilities(): void {
    this.facilityService.findAll().subscribe({
      next: (facilities) => this._facilities$.next(facilities),
      error: (err) => this.toastService.error('Could not load facilities', err),
    })
  }

  private loadMexusContext(): void {
    this.appContextService.activeContext$.subscribe({
      next: (ctx) => this._activeContext$.next(ctx),
      error: (err) => this.toastService.error('Could not load active mexus context', err),
    })
  }

  private setUpRequestBuildingPipeline(): void {
    combineLatest([this._activeContext$, this._facilities$])
      .pipe(
        takeUntilDestroyed(),
        filter(
          ([ctx, facilities]) =>
            ctx?.interval != null &&
            ctx?.liveTimestamp != null &&
            ctx?.viewMode != null &&
            facilities != null &&
            facilities.length > 0
        ),
        map(([ctx, facilities]) =>
          facilities.map((f) =>
            this.createOrUpdateAsyncFacilityUtilisation(
              f,
              ctx.interval,
              ctx.liveTimestamp,
              ctx.viewMode
            )
          )
        )
      )
      .subscribe((asyncUtils) => {
        this._utilisationsDataSrc.data = asyncUtils
        this.addToQueue(asyncUtils)
      })
  }

  private setUpRequestProcessingPipeline(): void {
    this._utilisationReloadQueue
      .pipe(
        takeUntilDestroyed(),
        filter(
          (asyncFacility) =>
            asyncFacility.state === AsyncFacilityUtilisationState.IDLE ||
            this.ctx.viewMode === MexusViewMode.PREVIEW
        ),
        tap((asyncFacility) => (asyncFacility.state = AsyncFacilityUtilisationState.LOADING)),
        mergeMap(
          (asyncFacility) =>
            asyncFacility.start().pipe(
              catchError((err) => of(err)) // Avoid killing queue
            ),
          1
        )
      )
      .subscribe({
        next: () => {},
        error: (err) => this.toastService.error('Data loading failed', err),
      })
  }

  private createOrUpdateAsyncFacilityUtilisation(
    facility: Facility,
    interval: Interval,
    timestamp: Instant,
    viewMode: MexusViewMode
  ): AsyncFacilityUtilisation {
    const existing = this.getExisting(facility?.id)
    if (existing) {
      existing.updateRequestFn(() =>
        this.buildLoadingRequest(facility.id, interval, timestamp, viewMode)
      )
      return existing
    } else {
      return this.buildAsyncFacilityUtilisation(facility, interval, timestamp, viewMode)
    }
  }

  private getExisting(facilityId: string): AsyncFacilityUtilisation {
    return this._utilisationsDataSrc.data.find((afu) => afu?.facility?.id === facilityId)
  }

  private buildAsyncFacilityUtilisation(
    facility: Facility,
    interval: Interval,
    timestamp: Instant,
    viewMode: MexusViewMode
  ): AsyncFacilityUtilisation {
    return new AsyncFacilityUtilisation(facility, () =>
      this.buildLoadingRequest(facility.id, interval, timestamp, viewMode)
    )
  }

  private buildLoadingRequest(
    facilityId: string,
    interval: Interval,
    timestamp: Instant,
    viewMode: MexusViewMode
  ): Observable<FacilityUtilisation> {
    return this.facilityUtilisationService.getFacilityUtilisation(
      facilityId,
      UtilisationFilterBuilder.start()
        .scheduleInterval(interval)
        .timestamp(timestamp)
        .viewMode(viewMode)
        .build()
    )
  }

  private openDetail(
    facility: Facility,
    scheduledInterval: Interval,
    timestamp: Instant,
    viewMode: MexusViewMode
  ): void {
    this.matDialog.open(FacilityUtilisationDetailDialogComponent, {
      minWidth: '100vw',
      height: '100vh',
      data: <IFacilityUtilisationDialogOptions>{
        facility: facility,
        scheduledInterval: scheduledInterval,
        timestamp: timestamp,
        viewMode: viewMode,
      },
    })
  }

  private addToQueue(loadingRequest: AsyncFacilityUtilisation[]): void {
    loadingRequest.forEach((lr) => this._utilisationReloadQueue.next(lr))
  }

  private cancelUtilisationRequest(): void {
    const sub = this._utilisationRequest
    if (sub) {
      sub.unsubscribe()
    }
  }
}
